// Mappa condivisa degli step di fulfillment di un ordine Poleepo lungo i 5
// step canonici: Pagato → In Lavorazione → Pronto → Spedito → Consegnato.
// Replica 1:1 del legacy
// ``Poleepo Manager App/ui/components.py::fulfillment_tracker``.
//
// Estratta da FulfillmentStepper.tsx (Task #7 2026-06-29) per essere
// riusata dalla timeline verticale del popup "Storico spedizione" nei
// Messaggi, senza duplicare la logica di mapping status→indice. Il
// FulfillmentStepper passa da resolveFulfillmentState come la timeline: una
// sola fonte di verita'.

export interface FulfillmentStep {
  status: string;
  label: string;
}

export const STEPS: ReadonlyArray<FulfillmentStep> = [
  { status: "PAYMENT_ACCEPTED", label: "Pagato" },
  { status: "WORK_IN_PROGRESS", label: "In lavoraz." },
  { status: "READY_TO_SHIP", label: "Pronto" },
  { status: "FULL_SHIPPED", label: "Spedito" },
  { status: "COMPLETED", label: "Consegnato" },
];

// Stati che lo stepper non sa disegnare. La fonte di verita' e'
// ``is_cancelled`` in backend/src/order_manager/modules/orders/mapper.py: qui
// stanno i letterali che il suo ``_STATUS_MAP`` manda su "annullato", e
// ``resolveFulfillmentState`` ripete il suo fallback (codice che contiene
// "CANCEL": CANCELLED_BY_SELLER, CANCELLED_BY_CUSTOMER e quelli che un
// marketplace inventera'). Unica aggiunta voluta: SHIPPED_WITH_ERROR, che per
// il backend e' "spedito" ma non e' un avanzamento da disegnare.
// ponytail: copia a mano del backend. Uno stato annullato nuovo si aggiunge
// prima nel mapper, poi qui: se succede spesso, la lista va servita dall'API.
export const NON_LINEAR: ReadonlySet<string> = new Set([
  "CANCELED",
  "CANCELLED",
  "REFUND",
  "REFUNDED",
  "PAYMENT_ERROR",
  "CANCEL_REQUESTED",
  "CANCEL_REJECTED",
  "PICKUP_IN_STORE",
  "SHIPPED_WITH_ERROR",
]);

/**
 * Indice dello step corrente per uno status Poleepo grezzo (gia' upper-case).
 * Ritorna -1 quando lo status precede il primo step rappresentato
 * (AWAITING_PAYMENT/PENDING) o non e' mappabile.
 */
export function statusToIndex(status: string): number {
  // PARTIALLY_SHIPPED → tra Pronto e Spedito (legacy mappa a indice 2,
  // post READY_TO_SHIP). Replichiamo lo stesso comportamento.
  if (status === "PARTIALLY_SHIPPED" || status === "PARTIALLY_SHIPPED_WAITING_PAYMENT") return 2;
  const idx = STEPS.findIndex((step) => step.status === status);
  // Stati intermedi non rappresentati come step (FULL_SHIPPED_WAITING_PAYMENT,
  // WAITING_FOR_CUSTOMER_CONFIRM, PENDING, AWAITING_PAYMENT, LOGISTIC_PROBLEM):
  // li teniamo all'indice piu' vicino senza saltare in avanti.
  if (idx === -1) {
    if (status === "FULL_SHIPPED_WAITING_PAYMENT") return 3;
    if (status === "AWAITING_PAYMENT" || status === "PENDING") return -1;
    if (status === "WAITING_FOR_CUSTOMER_CONFIRM") return 0;
    if (status === "LOGISTIC_PROBLEM") return 2;
  }
  return idx;
}

export interface FulfillmentState {
  /** Status normalizzato (upper-case, trimmed). Stringa vuota se assente. */
  status: string;
  /** True quando lo status non e' lineare (annullato/errore/reso): la
   *  timeline non e' rappresentabile, mostriamo solo lo stato grezzo. */
  isNonLinear: boolean;
  /** Indice dello step corrente (-1 se pre-step o non mappabile). */
  currentIndex: number;
  /** Label leggibile dello step corrente, derivata da STEPS. ``null`` quando
   *  lo stato e' vuoto, non-lineare o non mappato su uno step canonico. */
  currentLabel: string | null;
}

/**
 * Deriva lo stato di avanzamento (label + indice) da uno status Poleepo
 * grezzo. Unico punto in cui pill inline e timeline del popup leggono lo
 * "stato corrente", cosi' restano sempre allineati.
 */
export function resolveFulfillmentState(statusRaw: string | null | undefined): FulfillmentState {
  const status = (statusRaw ?? "").toUpperCase().trim();
  if (!status) {
    return { status: "", isNonLinear: false, currentIndex: -1, currentLabel: null };
  }
  if (NON_LINEAR.has(status) || status.includes("CANCEL")) {
    return { status, isNonLinear: true, currentIndex: -1, currentLabel: null };
  }
  const currentIndex = statusToIndex(status);
  const currentLabel = currentIndex >= 0 ? (STEPS[currentIndex]?.label ?? null) : null;
  return { status, isNonLinear: false, currentIndex, currentLabel };
}

/**
 * Formatta uno status Poleepo grezzo per la lettura umana quando NON e'
 * mappabile su uno step canonico (es. ``CANCELLED_BY_SELLER`` →
 * "Cancelled by seller"). Onesto: e' lo stato grezzo del marketplace, non
 * un evento di tracking.
 */
export function formatRawStatus(status: string): string {
  const cleaned = status.replace(/_/g, " ").trim().toLowerCase();
  if (!cleaned) return "—";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}
